import type React from "react";
import { useRouteError, isRouteErrorResponse, Link } from "react-router";
import { AlertCircle, Home, RefreshCw } from "lucide-react";
import { Button } from "@planta-v2/pds-ui/button";
import { i18n } from "@frontend/i18n";

const GENERIC_KEYS = {
  titleKey: "user.error.generic.title",
  descriptionKey: "user.error.generic.description",
} as const;

const NOT_FOUND_KEYS = {
  titleKey: "user.error.notFound.title",
  descriptionKey: "user.error.notFound.description",
} as const;

const SERVER_KEYS = {
  titleKey: "user.error.server.title",
  descriptionKey: "user.error.server.description",
} as const;

type ErrorKeys =
  | typeof GENERIC_KEYS
  | typeof NOT_FOUND_KEYS
  | typeof SERVER_KEYS;

const DEFAULT_ERROR_KEYS: ErrorKeys = GENERIC_KEYS;

export function RouteErrorBoundary(): React.JSX.Element {
  const error = useRouteError();

  let errorKeys: ErrorKeys = DEFAULT_ERROR_KEYS;

  if (isRouteErrorResponse(error)) {
    if (error.status === 404) {
      errorKeys = NOT_FOUND_KEYS;
    } else if (error.status === 500) {
      errorKeys = SERVER_KEYS;
    }
  }

  const { titleKey, descriptionKey } = errorKeys;

  console.error("[Router] Route error:", error);

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <AlertCircle className="h-16 w-16 text-destructive/50 mb-6" />
      <h1 className="text-xl font-semibold text-foreground mb-2">
        {i18n.t(titleKey)}
      </h1>
      <p className="text-sm text-muted-foreground text-center mb-6 max-w-md">
        {i18n.t(descriptionKey)}
      </p>
      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={(): void => {
            window.location.reload();
          }}
          className="gap-2"
        >
          <RefreshCw className="h-4 w-4" />
          {i18n.t("user.error.retryButton")}
        </Button>
        <Button asChild className="gap-2">
          <Link to="/">
            <Home className="h-4 w-4" />
            {i18n.t("user.error.homeButton")}
          </Link>
        </Button>
      </div>
    </div>
  );
}
